/**
 * App shell: gallery rendering, filter bar, and iframe-based activity router.
 *
 * Day-8 additions: shared settings popover + help overlay + progress dots,
 * splash-screen hand-off, privacy-friendly analytics. All shared bits live
 * in ./shared/polish.ts so each activity iframe can reuse them.
 */
import activitiesData from './data/activities.json';
import { progress, analytics, mountPolishTools } from './shared/polish';
import './shared/polish.css';

interface ActivityMeta {
  id: string;
  slug: string;
  title: string;
  blurb: string;
  cat: string;
  thumb: string;
}

const ACTIVITIES = activitiesData as ActivityMeta[];
const BY_ID = Object.fromEntries(ACTIVITIES.map((a) => [a.id, a]));

const galleryEl = document.getElementById('gallery') as HTMLElement;
const gridEl = document.getElementById('grid') as HTMLElement;
const slotEl = document.getElementById('activity-slot') as HTMLElement;
const filterBar = document.querySelector('.filter-bar') as HTMLElement;
const toolsEl = document.getElementById('tools') as HTMLElement;

/* ---------- tools (top-right controls) ---------- */
function renderTools(): void {
  toolsEl.innerHTML = '';
  mountPolishTools(toolsEl); // gallery context — no activity id
}

/* ---------- gallery rendering ---------- */
function cardHtml(a: ActivityMeta): string {
  const opened = progress.isOpened(a.id);
  const dotState = opened ? 'opened' : 'new';
  return `
    <a class="card" href="#${a.id}" data-cat="${a.cat}" data-id="${a.id}" aria-label="${a.title}">
      <span class="ef-progress-dot" data-state="${dotState}" aria-label="${opened ? 'Opened' : 'Not yet opened'}"></span>
      <div class="card-media">${a.thumb}</div>
      <div class="card-body">
        <span class="card-n">${a.id}</span>
        <h3 class="card-title">${a.title}</h3>
        <p class="card-blurb">${a.blurb}</p>
        <span class="card-cat" data-cat="${a.cat}">${a.cat}</span>
      </div>
    </a>
  `;
}

function renderGrid(filter: string = 'all'): void {
  const items = filter === 'all' ? ACTIVITIES : ACTIVITIES.filter((a) => a.cat === filter);
  gridEl.innerHTML = items.map(cardHtml).join('');
}

function refreshProgressDots(): void {
  for (const card of gridEl.querySelectorAll<HTMLElement>('.card')) {
    const id = card.dataset.id;
    if (!id) continue;
    const dot = card.querySelector<HTMLElement>('.ef-progress-dot');
    if (!dot) continue;
    dot.dataset.state = progress.isOpened(id) ? 'opened' : 'new';
  }
}

/* ---------- filter bar ---------- */
function wireFilters(): void {
  filterBar.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-filter]');
    if (!btn) return;
    const filter = btn.dataset.filter ?? 'all';
    for (const b of filterBar.querySelectorAll<HTMLButtonElement>('button[data-filter]')) {
      b.setAttribute('aria-pressed', String(b === btn));
    }
    renderGrid(filter);
    analytics.track('filter', { filter });
  });
}

/* ---------- router ---------- */
function route(): void {
  const id = decodeURIComponent(location.hash.slice(1));
  const activity = BY_ID[id];
  slotEl.innerHTML = '';
  if (activity) {
    galleryEl.hidden = true;
    document.title = `Activity ${id} · Exploring Forces`;
    const frame = document.createElement('iframe');
    frame.src = `./activities/${activity.slug}/index.html`;
    frame.title = activity.title;
    frame.setAttribute('allow', 'autoplay');
    frame.className = 'activity-frame';
    slotEl.appendChild(frame);
    try { frame.focus(); } catch {/* ignore */}
    progress.markOpened(id);
    analytics.track('activity.open', { id });
  } else {
    galleryEl.hidden = false;
    document.title = 'Exploring Forces — Classroom Companion';
    refreshProgressDots();
  }
  window.scrollTo(0, 0);
}

window.addEventListener('hashchange', route);
window.addEventListener('message', (e) => {
  // Activity iframes post { type: 'ef-nav', hash: '' } to return to the gallery.
  if (e.data && typeof e.data === 'object' && e.data.type === 'ef-nav') {
    location.hash = typeof e.data.hash === 'string' ? e.data.hash : '';
    return;
  }
  // Bridge: iframe-originated analytics events re-dispatched on the top
  // window so a single provider script in index.html catches both realms.
  if (e.data && typeof e.data === 'object' && e.data.type === 'ef-analytics' && e.data.detail) {
    window.dispatchEvent(new CustomEvent('ef-analytics', { detail: e.data.detail }));
  }
});
window.addEventListener('ef-progress-change', () => refreshProgressDots());

/* ---------- splash hand-off ---------- */
function hideSplash(): void {
  const splash = document.getElementById('ef-splash');
  if (!splash) return;
  splash.classList.add('ef-splash--hidden');
  // Fully remove after the transition so it won't trap pointer events if z-index changes.
  setTimeout(() => splash.remove(), 600);
}

/* ---------- boot ---------- */
renderTools();
renderGrid();
wireFilters();
route();
// Dissolve the splash once the DOM is wired. `window.load` fires after the
// service worker register + all stylesheets, so the first real paint looks
// fully styled before we fade. 120 ms fallback covers headless/backgrounded
// tabs where `load` can stall; rAF alone is not reliable there.
window.addEventListener('load', hideSplash, { once: true });
setTimeout(hideSplash, 120);
analytics.track('app.boot', { opened: progress.opened().size });

/* ---------- PWA update prompt ---------- */
function showUpdateToast(): void {
  if (document.querySelector('.ef-toast[data-kind="update"]')) return;
  const toast = document.createElement('div');
  toast.className = 'ef-toast';
  toast.dataset.kind = 'update';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <span class="ef-toast__icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 10a6 6 0 0 1-10.24 4.24M4 10a6 6 0 0 1 10.24-4.24"/><path d="M15 2v4h-4M5 18v-4h4"/></svg>
    </span>
    <p class="ef-toast__msg">A new version is ready.</p>
    <div class="ef-toast__actions">
      <button class="ef-toast__btn ef-toast__btn--ghost" data-act="later">Later</button>
      <button class="ef-toast__btn" data-act="update">Update</button>
    </div>
  `;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('ef-toast--open'));
  const dismiss = () => {
    toast.classList.remove('ef-toast--open');
    setTimeout(() => toast.remove(), 260);
  };
  toast.querySelector<HTMLButtonElement>('[data-act="update"]')?.addEventListener('click', () => {
    analytics.track('pwa.update', {});
    location.reload();
  });
  toast.querySelector<HTMLButtonElement>('[data-act="later"]')?.addEventListener('click', () => {
    analytics.track('pwa.update.dismiss', {});
    dismiss();
  });
}
window.addEventListener('ef-pwa-update-available', showUpdateToast);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      onNeedRefresh() {
        // Fires when a newer SW is waiting. Signal a non-blocking toast so
        // users can reload at a convenient moment; no blocking prompt.
        window.dispatchEvent(new CustomEvent('ef-pwa-update-available'));
      },
      onOfflineReady() {
        // Silent — the "offline-ready" state is invisible UX; a console log
        // adds DevTools noise on every load. Emit a window event so a future
        // first-run onboarding hint can react if needed.
        window.dispatchEvent(new CustomEvent('ef-pwa-offline-ready'));
      },
    });
  }).catch(() => {/* vite-plugin-pwa not available in dev */});
}
